import React, { createContext, useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import useApi, { ApiMethod } from '~/hooks/useApi';
import { EnableTypesToAccessApp } from '~/models/user';
import Endpoints from '~/services/endpoints';
import Constants from '~/utils/constants';
import { CustomizedError } from '~/utils/errors';

interface AuthState {
  token: string;
  user: object;
}

interface SignInCredentials {
  cpf: string;
  password: string;
}

export interface IAuthContext {
  user: any;
  userRoles: string[];
  signIn(credentials: SignInCredentials);
  signOut: ({ cpf }: { cpf?: string }) => void;
  isAuthenticated: () => boolean;
  handleSideBar: () => void;
  sidebarOpen: boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

const AuthContext = createContext<IAuthContext>({} as IAuthContext);

const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>({} as AuthState);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userRoles, setUserRoles] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(Constants.USER_ROLE)) || [];
    } catch {
      return [];
    }
  });
  const user = JSON.parse(localStorage.getItem('@NovaEra:user'));
  useEffect(() => {
    getUserDataOnStorage();
  }, []);

  const handleSideBar = () => setSidebarOpen(!sidebarOpen);

  const isAuthenticated = (): boolean => {
    const isAuth = JSON.parse(localStorage.getItem(Constants.IS_LOGGED));
    return isAuth?.isLogged || false;
  };

  const hasRole = (role: string): boolean => userRoles.includes(role);

  const hasAnyRole = (roles: string[]): boolean =>
    (roles || []).some((role) => userRoles.includes(role));

  const getUserDataOnStorage = (): void => {
    const user = JSON.parse(localStorage.getItem('@NovaEra:user'));
    if (user) setData((prev) => ({ ...prev, user }));
    const roles = JSON.parse(localStorage.getItem(Constants.USER_ROLE)) || [];
    setUserRoles(roles);
  };

  const AuthLogin = useApi<any>(Endpoints.account.userLogin, ApiMethod.POST);

  const AuthSignOut = useApi<any>(Endpoints.account.signOut, ApiMethod.POST);

  const signIn = async ({ cpf, password }) => {
    const payload = { cpf, senha: password };
    const response = await AuthLogin.callApi(payload);

    switch (response.status) {
      case 201:
        const { token, novoToken, ...user } = response.data;

        const isUser = user.perfis.some((el) =>
          EnableTypesToAccessApp.some((profileTypes) => profileTypes === el),
        );

        if (!isUser) {
          throw CustomizedError({
            message: 'Você não possui permissão para prosseguir.',
          });
        }

        localStorage.setItem(Constants.TOKEN_KEY, token);

        localStorage.setItem(Constants.TOKEN_BEARER, novoToken);

        localStorage.setItem(Constants.USER, JSON.stringify(user));

        localStorage.setItem(
          Constants.USER_ROLE,
          JSON.stringify(response.data.perfis || []),
        );

        localStorage.setItem(Constants.USER_ID, response.data.id);

        localStorage.setItem(
          Constants.IS_LOGGED,
          JSON.stringify({ isLogged: true }),
        );

        unstable_batchedUpdates(() => {
          setData((prev) => ({ ...prev, user: response.data }));
          setUserRoles(response.data.perfis || []);
        });
        return { status: 201, data: response.data };

      case 401:
        throw CustomizedError({
          message:
            response.data.mensagem ??
            'Ops! Serviço indisponível no momento. Tente novamente mais tarde.',
        });

      case 422:
        return { status: 422, message: response.data.mensagem };

      default:
        throw CustomizedError({
          message: response.data.mensagem || response.status,
        });
    }
  };

  const signOut = async ({ cpf }: { cpf?: string }) => {
    try {
      const response = await AuthSignOut.callApi({
        cpf: cpf ?? user?.cpf ?? data?.user?.cpf,
      });
      if (response.status >= 200 && response.status < 300) {
        localStorage.clear();
        setUserRoles([]);
        window.location.href = '/';
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        userRoles,
        signIn,
        signOut,
        isAuthenticated,
        handleSideBar,
        sidebarOpen,
        hasRole,
        hasAnyRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
